<template>
  <div>

    <table-tools
      :actions="[{title: $t('titles.download_csv'), icon: '$download', click: openDownloadCsvModal, busy: generatingCsv}]"
      :headers="headers"
      v-model:enabled-headers="enabledHeaders"
      remember-headers="transactions-report-new2"
    >

      <table-filter>
        <o-select
          :label="$t('titles.filter_by')"
          v-model="formState.mode"
          :items="[{value: 'on_lot_date', title: 'On-Lot Date'}, {value: 'transaction_date', title: 'Transaction Date'}, {value: 'payout_date', title: 'Payout Date'}]"
        />
       </table-filter>

      <table-filter>
        <o-text-field
          :label="$t('titles.channel_id')"
          v-model="pendingFormState.transactionId"
          clearable
          @blur="() => formState.transactionId = pendingFormState.transactionId"
        />
      </table-filter>

      <table-filter>
        <o-text-field
          :label="$t('titles.license_plate')"
          v-model="pendingFormState.licensePlate"
          clearable
          @blur="() => formState.licensePlate = pendingFormState.licensePlate"
        />
      </table-filter>

      <table-filter>
        <o-select
          :label="$t('titles.status')"
          v-model="formState.status"
          :items="[{value: 'cancelled', title: 'Canceled'}, {value: 'valid', title: 'Valid'}]"
          clearable
        />
      </table-filter>

      <table-filter>
        <o-select
          :label="$t('titles.verticals')"
          v-model="pendingFormState.verticals"
          :items="VerticalRegistry.getOptions()"
          multiple
          clearable
          @blur="() => formState.verticals = pendingFormState.verticals"
        />
      </table-filter>

      <table-filter>
        <o-autocomplete
          :label="$t('titles.products')"
          v-model="pendingFormState.locationProductIds"
          multiple
          :items="locationProductOptions"
          clearable
          @blur="() => formState.locationProductIds = pendingFormState.locationProductIds"
        />
      </table-filter>

    </table-tools>

    <p class="mb-8">All times are shown in the location's local time zone.</p>

    <v-data-table-server
      :items-per-page="25"
      :items-per-page-options="[{value: 10, title: '10'}, {value: 25, title: '25'}, {value: 50, title: '50'}, {value: 100, title: '100'}]"
      :items="transactions"
      :items-length="totalItems"
      :headers="visibleHeaders"
      :loading="loading"
      @update:options="loadItems"
      v-model:sort-by="sortBy"
      :search="tableSearch"
    >

      <template v-slot:item.start_time_local="{ value }">
        {{ $d(value, 'ymd_hm') }}
      </template>

      <template v-slot:item.end_time_local="{ value }">
        {{ value ? $d(value, 'ymd_hm') : '' }}
      </template>

      <template v-slot:item.status="{ value }">
        {{ TransactionStatuses[value] ?? value }}
      </template>

      <template v-slot:item.location_product_ids="{ value }">
        <template v-for="(location_product_id, i) in value"><br v-if="i > 0" />{{ getProductLabel(location_product_id) }}</template>
      </template>

      <template v-slot:item.transaction_date_local="{ value }">
        {{ $d(value, 'ymd_hms') }}
      </template>

      <template v-slot:item.payout_date_local="{ value }">
        {{ value ? $d(value, 'ymd') : '' }}
      </template>

      <template v-slot:item.channel="{ value }">
        {{ ChannelRegistry.find(value)?.titleShort ?? value }}
      </template>

      <template v-slot:item.vertical="{ value }">
        {{ VerticalRegistry.getTitle(value) }}
      </template>

      <template v-slot:item.gross_revenue="{ value }">
        {{ value ? $n(value, 'currency') : '' }}
      </template>

      <template v-slot:item.net_revenue="{ value }">
        {{ value ? $n(value, 'currency') : '' }}
      </template>

    </v-data-table-server>

  </div>
</template>

<script setup lang="ts">

import { computed, inject, reactive, ref, watch } from 'vue'
import { useTitle } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import { QuerySyncer, CommaSeparatedSerializer, CommaSeparatedNumberSerializer } from '../../../utils/QuerySyncer'

import TransactionsDownloadModal from './TransactionsDownloadModal.vue'

// models
import Location from '../../../models/Location'
import LocationProduct from '../../../models/LocationProduct'

// misc
import axios from 'axios'
import { ReportingApi } from '../../../services/api-clients/ReportingApi'
import { TransactionStatuses } from '../../../enums'
import ChannelRegistry from '../../../channels/ChannelRegistry'
import NotificationService from '../../../services/NotificationService'
import VerticalRegistry from '../../../verticals/VerticalRegistry'
// @ts-ignore
import { openDialog } from 'vue3-promise-dialog'
import type { Can } from '../../../main'
import type { VAutocompleteItem, VDataTableServerHeader } from '../../../types/vuetify'
import type { TransactionsDownloadOptions } from './types'
import type { DataTableSortItem } from 'vuetify'
import { Temporal } from '@js-temporal/polyfill'

interface Props {
  locationIds: number[]
  channels: string[]
  start: null | Temporal.PlainDate
  end: null | Temporal.PlainDate
}

const props = withDefaults(defineProps<Props>(), {
  channels: () => [],
})

const $can = inject('$can') as Can
const canSeeIds = $can('seeids')

const { t } = useI18n()

const title = useTitle()
title.value = t('titles.reporting_transactions')

/**
 * Form
 */

const formState = reactive({
  mode: 'payout_date',
  transactionId: null,
  licensePlate: null,
  status: null,
  verticals: null,
  locationProductIds: [],
})

// Some of the filters shouldn't trigger an API load on every change (keypress,
// checkbox check/uncheck, etc.) We'll cache the changed values in this object,
// and only apply them when the corresponding control loses focus.
const pendingFormState = reactive({
  transactionId: formState.transactionId,
  licensePlate: null,
  verticals: formState.verticals,
  locationProductIds: [],
})

const locationProductOptions = computed<VAutocompleteItem[]>(() => {

  if (!props.locationIds) {
    return []
  }

  const locations = Location.query()
    .whereIdIn(props.locationIds)
    .has('locationProducts')
    .with('locationProducts', (query) => {
      query.with('product').orderBy('product.name')
    })
    .orderBy('locationName')
    .get()

  const options: VAutocompleteItem[] = []

  locations.forEach((location) => {
    location.locationProducts.forEach((lp) => {
      options.push({
        value: lp.ID,
        // TODO: IDs
        title: `${location.locationName}: ${lp.product.name}`,
      })
    })
  })

  return options

})

const activeFilters = computed(() => {

  const filters = {
    location_ids: props.locationIds,
    channels: props.channels,
    start: props.start,
    end: props.end,
    mode: formState.mode,
    transaction_id: formState.transactionId,
    license_plate: formState.licensePlate,
    status: formState.status,
    verticals: formState.verticals,
    location_product_ids: formState.locationProductIds,
  }

  // if (formState.transactionId) {
  //   filters.transaction_id = formState.transactionId
  // }

  // if (formState.status) {
  //   filters.status = formState.status
  // }

  // if (formState.verticals) {
  //   filters.verticals = formState.verticals
  // }

  return filters

})

watch([
  () => props.locationIds,
  () => props.channels,
  () => props.start,
  () => props.end,
  // we can't just watch the entire formState object, because that causes unwanted and inexplicable triggers of the watcher
  () => formState.mode,
  () => formState.transactionId,
  () => formState.licensePlate,
  () => formState.status,
  () => formState.verticals,
  () => formState.locationProductIds,
], () => {
  tableSearch.value = Math.random().toString()
})

/**
 * Sync formState to the querystring (and vice-versa)
 */

new QuerySyncer({
  transactions_mode: {
    watchable: () => formState.mode,
    hydrate: (newValue) => formState.mode = newValue,
  },
  transactions_transaction_id: {
    watchable: () => formState.transactionId,
    hydrate: (newValue) => {
      formState.transactionId = newValue
      pendingFormState.transactionId = newValue
    },
  },
  transactions_license_plate: {
    watchable: () => formState.licensePlate,
    hydrate: (newValue) => {
      formState.licensePlate = newValue
      pendingFormState.licensePlate = newValue
    },
  },
  transactions_status: {
    watchable: () => formState.status,
    hydrate: (newValue) => formState.status = newValue,
  },
  transactions_verticals: {
    watchable: () => formState.verticals,
    hydrate: (newValue) => {
      formState.verticals = newValue
      pendingFormState.verticals = newValue
    },
    serializer: CommaSeparatedSerializer,
  },
  location_product_ids: {
    watchable: () => formState.locationProductIds,
    hydrate: (newValue) => {
      formState.locationProductIds = newValue
      pendingFormState.locationProductIds = newValue
    },
    serializer: CommaSeparatedNumberSerializer,
  },
})

/**
 * Data table
 */

const headers = computed<VDataTableServerHeader[]>(() => [
  {key: 'id', title: t('titles.ocra_id'), hidden: !canSeeIds},
  {key: 'channel', title: t('titles.channel')},
  {key: 'transaction_id', title: t('titles.channel_id'), toggleable: false},
  {key: 'start_time_local', title: t('titles.start')},
  {key: 'end_time_local', title: t('titles.end')},
  {key: 'license_plate', title: t('titles.license_plate'), sortable: false},
  {key: 'status', title: t('titles.status')},
  {key: 'location_product_ids', title: t('titles.products'), sortable: false},
  {key: 'transaction_date_local', title: t('titles.transaction_date_time')},
  {key: 'payout_date_local', title: t('titles.payout_date')},
  {key: 'vertical', title: t('titles.vertical')},
  {key: 'gross_revenue', title: t('titles.gross_revenue'), align: 'end'},
  {key: 'net_revenue', title: t('titles.net_revenue'), align: 'end'},
])

const enabledHeaders = ref<string[]>([
  'location_id', 'channel', 'transaction_id', 'start_time_local', 'end_time_local',
  'license_plate', 'status', 'location_product_ids', 'transaction_date_local', 'payout_date_local',
  'vertical', 'gross_revenue', 'net_revenue',
])

if (canSeeIds) {
  enabledHeaders.value.push('id')
}

const visibleHeaders = computed<VDataTableServerHeader[]>(() => {
  return headers.value.filter(header => enabledHeaders.value.includes(header.key as string))
})

const transactions = ref([])
const totalItems = ref(0)
const sortBy = ref<DataTableSortItem[]>([{ key: 'start_time_local', order: 'desc' }])
// sort of a kludge. updating this value forces the table to reload data from the server. it doesn't actually have anything to do with search.
const tableSearch = ref<string>('')

const loading = ref(false)
const pendingRequestAbortControllers: AbortController[] = []

function loadItems(options: any) {

  if (props.locationIds.length === 0 || !props.start || !props.end) {
    return
  }

  pendingRequestAbortControllers.forEach((controller) => {
    controller.abort()
  })
  pendingRequestAbortControllers.length = 0

  const abortController = new AbortController()
  pendingRequestAbortControllers.push(abortController)
  loading.value = true

  ReportingApi
    .getTransactions({
      filters: activeFilters.value,
      limit: options.itemsPerPage,
      offset: (options.page - 1) * options.itemsPerPage,
      sorts: options.sortBy,
      signal: abortController.signal,
    })
    .then((response) => {
      totalItems.value = response.count
      transactions.value = response.results
    })
    .catch((error) => {
      if (axios.isCancel(error)) {
        return
      }
      console.error(error)
      NotificationService.error(t('alerts.couldnt_load_transactions'))
    })
    .finally(() => {
      loading.value = false
    })

}

/**
 * CSV download
 */

const generatingCsv = ref(false)

function openDownloadCsvModal() {

  openDialog(TransactionsDownloadModal)
    .then((options: TransactionsDownloadOptions) => {

      if (!options) {
        return
      }

      generatingCsv.value = true

      ReportingApi
      .getTransactions({
        filters: {...activeFilters.value, format: 'csv', include_summary_row: options.include_summary_row},
        sorts: [{ key: 'id', order: 'asc' }],
      })
      .then((response) => {
        window.location = response.csv_url
      })
      .catch((error) => {
        console.error(error)
        NotificationService.error(t('alerts.csv_download_failed'))
      })
      .finally(() => {
        generatingCsv.value = false
      })

    })

}

/**
 *
 */

function getProductLabel(locationProductId: number) {
  // TODO: this is hella inefficient. fix that.
  const lp = LocationProduct.query().where('ID', locationProductId).with(['product', 'location']).first()
  return lp ? `${lp.location.locationName}: ${lp.product.name}` : locationProductId
}

</script>
